import React, { Component, ErrorInfo, ReactNode } from "react";
import courseIcon from "assets/icons/icon-course.svg";
import { Box, Typography, Button as MuiButton, Theme } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import portalSettingsManager from "utils/portalSettingsManager";

const Figure = styled("figure")({
  display: "flex",
  flexDirection: "column",
  margin: "auto",
});

const ImageIcon = styled("img")({
  height: "89px",
  opacity: "0.5",
  marginBottom: "10px",
});

const Button = styled(MuiButton)(({ theme }) => ({
  borderRadius: 0,
  color: theme.palette.primary.main,
  width: "143px",
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
  height: "42px",
  margin: "0 auto",
  fontSize: 11,
  fontWeight: 700,
  "&.MuiButton-outlined": {
    border: `1px solid ${theme.palette.primary.main}`,
  },
  "&.Mui-disabled": {
    color: theme.palette.grey["500"],
  },
}));

interface Props {
  children: ReactNode;
  theme: Theme;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  handleGoBack = (): void => {
    window.history.back();
  };

  handleRefresh = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Box display="flex" flexDirection="column" padding="45px 51px">
          <Box>
            <Figure>
              <ImageIcon src={courseIcon} alt="Error" />
            </Figure>
          </Box>
          <Box style={{ textAlign: "center", marginBottom: "67px" }}>
            <Typography
              style={{
                fontSize: "50px",
                color:
                  portalSettingsManager.buttonColors?.themed?.active?.text ||
                  this.props.theme.palette.primary.main,
                letterSpacing: "1.25px",
                fontWeight: 700,
              }}
            >
              SOMETHING WENT WRONG
            </Typography>
            <Typography
              style={{
                maxWidth: "861px",
                margin: "0 auto",
                fontSize: "26px",
                color: this.props.theme.palette.grey["500"],
                letterSpacing: 0,
                textAlign: "center",
                lineHeight: "39px",
                fontWeight: 400,
              }}
            >
              We apologize for the inconvenience. An unexpected error has occurred. Please try
              refreshing the page or go back to the previous page.
            </Typography>
          </Box>
          <Box display="flex" justifyContent="center" gap="16px">
            <Button variant="outlined" onClick={this.handleRefresh}>
              Refresh
            </Button>
            <Button variant="outlined" onClick={this.handleGoBack}>
              Go Back
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

export const ErrorBoundary = ({ children }: { children: ReactNode }): React.ReactElement => {
  const theme = useTheme();
  return <ErrorBoundaryClass theme={theme}>{children}</ErrorBoundaryClass>;
};
